import { ComponentInterface, Entity, Time } from '@goodboydigital/odie';
import { Spine } from '@pixi-spine/loader-uni';
import { Container } from '@pixi/display';
import { SpineSprite } from 'pixi-spine';
import { SignalConnection } from 'typed-signals';

import { Shared } from '../../App';
import { CharacterEntity } from '../entities/CharacterEntity';

export class SpineComponent implements ComponentInterface
{
    public character!: CharacterEntity;
    public spineBackContainer?: Container;
    public spineBack?: Spine;
    public spine?: Spine;
    
    public animName = '';
    public bikeMode = false;
    
    private onGamePauseSignalConnection?: SignalConnection;
    private onGameResumeSignalConnection?: SignalConnection;

    constructor()
    {
        
    }

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof CharacterEntity)) throw new Error('Spine component only works with CharacterEntity');
        this.character = entity as CharacterEntity;
        this.character.physics.physicsFixedUpdateRunner.add(this as any);
    }

    public setup(name: string, { x = 0, y = 0, scale = 1, bikeMode = false, skin = "" } = {}): void
    {
        if(this.spine) return;

        this.clear();
        this.spineBackContainer = new Container();
        this.spineBack = new Spine(Shared.data.getSkeletonData(name));
        this.spine = new Spine(Shared.data.getSkeletonData(name));
        
        this.spineBackContainer.addChild(this.spineBack);
        this.character.view.addChild(this.spineBackContainer);
        this.character.view.addChild(this.spine);
        this.spineBack.scale.set(scale);
        this.spine.scale.set(scale);
        if(skin){
            this.spineBack.skeleton.setSkinByName(skin);
            this.spine.skeleton.setSkinByName(skin);
        }
        
        this.spineBack.x = x;
        this.spineBack.y = y;
        this.spine.x = x;
        this.spine.y = y;
        
        this.spineBack.autoUpdate = true;
        this.spine.autoUpdate = true;
        
        this.onGamePauseSignalConnection = Shared.game.onPause.connect(() => this.onGamePause());
        this.onGameResumeSignalConnection = Shared.game.onResume.connect(() => this.onGameResume());
        
        this.bikeMode = bikeMode;
        this.playCelebrate();
        
    }

    public clear(): void
    {
        if (!this.spine) return;
        if (this.spine.parent) this.spine.parent.removeChild(this.spine);
        this.spine.destroy();
        this.spine = undefined;
        
        if (!this.spineBack) return;
        if (this.spineBack.parent) this.spineBack.parent.removeChild(this.spineBack);
        this.spineBack.destroy();
        this.spineBack = undefined;
        
        if (!this.spineBackContainer) return;
        if (this.spineBackContainer.parent) this.spineBackContainer.parent.removeChild(this.spineBackContainer);
        this.spineBackContainer.destroy();
        this.spineBackContainer = undefined;
        
        this.animName = '';

        this.onGamePauseSignalConnection?.disconnect();
        this.onGameResumeSignalConnection?.disconnect();
    }
    
    public physicsFixedUpdate(): void
    {
        if(this.spineBackContainer && this.spine && this.spineBack && !this.character.replay.suspendPlayback){
            this.spineBackContainer.x = this.spine.parent.x + this.spine.x - this.spineBack.x;
            this.spineBackContainer.y = this.spine.parent.y + this.spine.y - this.spineBack.y;
            this.spineBackContainer.angle = this.spine.parent.angle;
        }
        
    }
    
    public playIdle(): void
    {
        if (!this.spine) return;
        this.playAnimation('Idle', true);
        SpineComponent.updateLegs(this.spine, false, this.bikeMode, this.spineBack);
    }

    public playCelebrate(): void
    {
        if (!this.spine) return;

        this.playAnimation('Celebrate', true);
        SpineComponent.updateLegs(this.spine, false, this.bikeMode, this.spineBack);
    }

    public playFall(): void
    {
        if (!this.spine) return;
        this.playAnimation('crash', true); // No good
        // this.playAnimation('Idle');
        SpineComponent.updateLegs(this.spine, true, true, this.spineBack);
    }

    public playAnimation(name: string, loop: boolean): void
    {
        if (!this.spine || this.animName === name) return;
        if (!this.spine.state.hasAnimation(name)) name = name.toLowerCase();
        if (!this.spine.state.hasAnimation(name)) return;
        this.animName = name;
        this.spineBack?.state.setAnimation(0, name, loop);
        this.spine.state.setAnimation(0, name, loop);
    }

    public static updateLegs(spine: Spine, leftVisible: boolean, rightVisible: boolean, backLegsSpine?: Spine): void
    {
        if (!spine) return;
        
        for (const slot of spine.skeleton.slots)
        {
            if (slot.currentMesh && slot.currentMeshName && slot.currentMeshName.includes('rear-leg'))
            {
                slot.currentMesh.renderable = leftVisible;
            }
            else if (slot.currentMesh && slot.currentMeshName && slot.currentMeshName.includes('fore-leg'))
            {
                slot.currentMesh.renderable = rightVisible;
            }
            if (slot.currentSprite && slot.currentSpriteName && slot.currentSpriteName.includes('rear-leg'))
            {
                slot.currentSprite.renderable = leftVisible;
            }
            else if (slot.currentSprite && slot.currentSpriteName && slot.currentSpriteName.includes('fore-leg'))
            {
                slot.currentSprite.renderable = rightVisible;
            }
        }
        
        if(backLegsSpine){
            backLegsSpine.renderable = !leftVisible && rightVisible;
            
            if(backLegsSpine.renderable){
                
                for(const children of backLegsSpine.children){
                    let child = children as Container;
                    if(child.children.length == 0){
                        child.renderable = false;
                    }
                    for(const spineSpriteChildren of child.children){
                        let spineSprite = spineSpriteChildren as SpineSprite;
                        children.renderable = spineSprite.attachment?.name.includes('rear-leg') || false;
                    }
                    
                }
            }
            
        }
        
    }
    
    public static getSpineSprite(name: string, { x = 0, y = 0, scale = 1, bikeMode = false, anim = 'Idle', skin = "" } = {}): Container
    {
        const container = new Container();

        const spine = new Spine(Shared.data.getSkeletonData(name));

        container.addChild(spine);
        spine.scale.set(scale);
        spine.x = x;
        spine.y = y;
        spine.autoUpdate = true;
        if(skin){
            spine.skeleton.setSkinByName(skin);
        }
        
        if (spine.state.hasAnimation(anim)) spine.state.setAnimation(0, anim, true);
        if (spine.state.hasAnimation(anim.toLowerCase())) spine.state.setAnimation(0, anim.toLowerCase(), true);

        SpineComponent.updateLegs(spine, false, bikeMode);

        return container;
    }
    
    private onGamePause(){
        if(this.spineBack){
            this.spineBack.autoUpdate = false;
        }
        if(this.spine){
            this.spine.autoUpdate = false;
        }
        
    }
    
    private onGameResume(){        
        if(this.spineBack){
            this.spineBack.autoUpdate = true;
        }
        if(this.spine){
            this.spine.autoUpdate = true;
        }
    }
    
    public cleanup(): void
    {
        this.clear();
    }
}
